import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Beaker, Target, Pencil, Trash2, FileDown, Award, ArrowUpRight, Leaf, UserCheck,
} from "lucide-react";
import { DECISION_COLORS, DECISION_LABELS } from "./pilotUtils";
import { InnoHubRefIcon } from "../shared/InnoHubRefLink";

export function PilotCard({
  pilot, getInitiativeName, getPartnerName, getInitiativeStatus,
  onEdit, onDelete, onDecision, onDownloadFicha, onOpenCierre, onPromoteToProduction,
}) {
  const isProspecting = getInitiativeStatus && getInitiativeStatus(pilot.initiative_id) === "idea";
  return (
    <Card className="card-hover">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Beaker className="w-5 h-5 text-primary" />
          </div>
          <div className="flex items-center gap-2">
            <InnoHubRefIcon refs={pilot.refs} />
            <span className={`status-badge ${DECISION_COLORS[pilot.decision]}`}>
              {DECISION_LABELS[pilot.decision]}
            </span>
          </div>
        </div>
        <CardTitle className="text-lg mt-3">{pilot.name}</CardTitle>
        <CardDescription className="flex items-center gap-1.5 flex-wrap">
          {getInitiativeName(pilot.initiative_id)}
          {pilot.initiative_link_source === "manual_review" && (
            <Badge
              variant="outline"
              className="text-[10px] py-0 px-1.5 gap-1 border-amber-300 text-amber-700 dark:text-amber-400"
              title="El vínculo a esta iniciativa fue asignado manualmente tras revisión editorial (no es un dato importado automáticamente)"
              data-testid={`pilot-manual-link-badge-${pilot.id}`}
            >
              <UserCheck className="w-2.5 h-2.5" />Vinculado manualmente
            </Badge>
          )}
          {isProspecting && (
            <Badge
              variant="outline"
              className="text-[10px] py-0 px-1.5 gap-1 border-orange-300 text-orange-700 dark:text-orange-400"
              title="La iniciativa vinculada todavía está en fase 'Idea' (prospección/primer contacto) — este piloto aún no ha empezado en la práctica."
              data-testid={`pilot-prospecting-badge-${pilot.id}`}
            >
              Aún en prospección
            </Badge>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {pilot.objective && (
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Objetivo</p>
            <p className="text-sm line-clamp-2">{pilot.objective}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Partner</p>
            <p className="font-medium">{getPartnerName(pilot.partner_id)}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Duración</p>
            <p className="font-medium">{pilot.duration_weeks} semanas</p>
          </div>
          {pilot.start_date && (
            <div>
              <p className="text-muted-foreground">Inicio</p>
              <p className="font-medium">{String(pilot.start_date).slice(0, 10)}</p>
            </div>
          )}
          {pilot.end_date && (
            <div>
              <p className="text-muted-foreground">Fin</p>
              <p className="font-medium">{String(pilot.end_date).slice(0, 10)}</p>
            </div>
          )}
        </div>

        {pilot.cost_estimated && (
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex justify-between text-sm">
              <span>Coste estimado</span>
              <span className="font-mono font-medium">€{pilot.cost_estimated.toLocaleString()}</span>
            </div>
            {pilot.cost_actual && (
              <div className="flex justify-between text-sm mt-1">
                <span>Coste real</span>
                <span className="font-mono font-medium">€{pilot.cost_actual.toLocaleString()}</span>
              </div>
            )}
            {pilot.real_cost_eur > 0 && (
              <div className="flex justify-between text-sm mt-1 text-red-700">
                <span>Coste real €</span>
                <span className="font-mono font-medium">€{pilot.real_cost_eur.toLocaleString()}</span>
              </div>
            )}
            {pilot.effort_hours_total > 0 && (
              <div className="flex justify-between text-sm mt-1 text-emerald-700">
                <span>Esfuerzo</span>
                <span className="font-mono font-medium">{pilot.effort_hours_total}h</span>
              </div>
            )}
          </div>
        )}

        {(pilot.sponsor || pilot.business_line || (pilot.tasks || []).length > 0 || pilot.cierre_propuesta_presentada) && (
          <div className="text-xs text-muted-foreground space-y-1" data-testid={`pilot-enriched-${pilot.id}`}>
            {pilot.sponsor && <div>👤 <span className="font-medium">Sponsor:</span> {pilot.sponsor}</div>}
            {pilot.business_line && <div>🏭 <span className="font-medium">Línea:</span> {pilot.business_line}</div>}
            {(pilot.tasks || []).length > 0 && (
              <div>
                📋 <span className="font-medium">Tareas:</span> {(pilot.tasks || []).filter(t => t.status === "done").length}/{(pilot.tasks || []).length} completadas
              </div>
            )}
            {pilot.cierre_propuesta_presentada && (
              <div className="text-emerald-700 dark:text-emerald-400" data-testid={`pilot-propuesta-badge-${pilot.id}`}>
                ✅ <span className="font-medium">Propuesta presentada</span>{pilot.cierre_propuesta_fecha ? ` · ${pilot.cierre_propuesta_fecha}` : ""}
                {pilot.cierre_propuesta_doc_url && (
                  <a href={pilot.cierre_propuesta_doc_url} target="_blank" rel="noreferrer" className="ml-1 underline">doc</a>
                )}
              </div>
            )}
          </div>
        )}

        {pilot.decision === "pending" ? (
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onEdit(pilot)}>
                <Pencil className="w-4 h-4 mr-1" />Editar
              </Button>
              <Button className="flex-1" onClick={() => onDecision(pilot)}>
                <Target className="w-4 h-4 mr-2" />Decisión
              </Button>
            </div>
            <Button
              variant="outline" size="sm"
              className="w-full text-xs"
              data-testid={`pilot-ficha-pdf-${pilot.id}`}
              onClick={() => onDownloadFicha(pilot.id, pilot.name, "ficha")}
            >
              <FileDown className="w-4 h-4 mr-2" />Ficha PDF
            </Button>
            <Button
              variant="outline" size="sm"
              className="w-full text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              data-testid={`pilot-ficha-comercial-${pilot.id}`}
              onClick={() => onDownloadFicha(pilot.id, pilot.name, "comercial")}
            >
              <Leaf className="w-4 h-4 mr-2 text-emerald-600" />Ficha Comercial
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onEdit(pilot)} data-testid={`pilot-edit-${pilot.id}`}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => onDelete(pilot.id, pilot.name)} data-testid={`pilot-delete-${pilot.id}`}>
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
              <Button
                variant="outline" size="sm"
                className="flex-1 text-xs"
                data-testid={`pilot-ficha-pdf-${pilot.id}`}
                onClick={() => onDownloadFicha(pilot.id, pilot.name, "ficha")}
              >
                <FileDown className="w-4 h-4 mr-1" />Ficha
              </Button>
              <Button
                variant="outline" size="sm"
                className="flex-1 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid={`pilot-ficha-comercial-compact-${pilot.id}`}
                onClick={() => onDownloadFicha(pilot.id, pilot.name, "comercial")}
              >
                <Leaf className="w-4 h-4 mr-1 text-emerald-600" />Comercial
              </Button>
            </div>
            <Button
              variant="outline" size="sm"
              className="w-full text-xs border-amber-200 text-amber-700 hover:bg-amber-50"
              data-testid={`pilot-cierre-modal-${pilot.id}`}
              onClick={() => onOpenCierre(pilot)}
            >
              <Award className="w-4 h-4 mr-2 text-amber-500" />Ficha de Cierre
            </Button>
          </div>
        )}

        {pilot.decision === "scale" && (
          <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950/30 rounded-lg text-green-700 dark:text-green-400">
            <ArrowUpRight className="w-4 h-4" />
            <span className="text-sm font-medium flex-1">Plan de transferencia generado</span>
            {onPromoteToProduction && (
              <Button
                size="sm"
                variant="outline"
                className="border-emerald-300 bg-white hover:bg-emerald-100 text-emerald-700 text-xs"
                onClick={() => onPromoteToProduction(pilot)}
                data-testid={`pilot-promote-production-${pilot.id}`}
              >
                Promover a Producción →
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
